import { memo, useCallback, useMemo, useState, type ReactNode, type CSSProperties } from 'react';
import ReactSelect from 'react-select';
import { areEqualFields } from 'utils/form';
import get from 'lodash/get';
import flatten from 'lodash/flatten';
import requireValue from 'utils/requireValue';
import { spliceLabelOnMobile } from '../../utils/helpers';
import { SelectCustomStyles } from './customStyle';
import type { TFieldComponentProps } from './types';
import s from './Select.module.scss';

export interface ISelectOption {
  label: ReactNode;
  value: string;
  options?: undefined;
}

export interface ISelectOptionGroup {
  label: ReactNode;
  options: ISelectOption[];
  value?: undefined;
}

export interface ISelectProps extends TFieldComponentProps<string | null | undefined> {
  /** Formik does not set it, see the default option below. */
  field: TFieldComponentProps<string | null | undefined>['field'] & { defaultValue?: string };
  label?: ReactNode;
  /** The options, or with `grouped` the option groups. */
  options?: Array<ISelectOption | ISelectOptionGroup>;
  style?: CSSProperties;
  isSearchable?: boolean;
  note?: ReactNode;
  grouped?: boolean;
  required?: boolean;
}

const Select = ({
  field, // { name, value, onChange, onBlur }
  form: { touched, errors, setFieldValue }, // also values, setXXXX, handleXXXX, dirty, isValid, status, etc.
  ...props
}: ISelectProps) => {
  const { label, options = [], style, isSearchable = false, note, grouped } = props;
  const [inputValue, setInputValue] = useState<string>();

  const handleInputChange = useCallback((v: string) => {
    setInputValue(v);
  }, []);

  // const handleBlur = useCallback(() => {
  //   setFieldTouched(field.name);
  // }, [field, setFieldTouched]);

  const handleChange = useCallback(
    (selectedOption: ISelectOption | ISelectOptionGroup | null | undefined) => {
      const selectedValue = get(selectedOption, 'value', null);
      setFieldValue(field.name, selectedValue);
    },
    [setFieldValue, field.name],
  );

  const optionValue = useMemo(() => {
    // Bug: formik's `field` has no `defaultValue`, so the default option is never selected here.
    const defaultOption = field.defaultValue
      ? options.find((o) => o.value && o.value === field.defaultValue)
      : null;
    if (defaultOption) {
      handleChange(defaultOption);
      return defaultOption;
    }
    // Without option groups `grouped` flattens undefined entries, and the lookup throws.
    const _options: Array<ISelectOption | ISelectOptionGroup | undefined> = grouped
      ? flatten(options.map(({ options: o }) => o))
      : options;
    const option = _options.find((o) => requireValue(o, 'value').value === field.value);
    return option || null;
  }, [field.defaultValue, field.value, options, grouped, handleChange]);

  const classNameForLabel = useMemo(
    () => (field.value || inputValue ? 'label-when-focused-input' : s.label),
    [field.value, inputValue],
  );

  return (
    <div className="field-wrapper" style={style}>
      <ReactSelect
        inputValue={inputValue}
        onInputChange={handleInputChange}
        inputProps={{ autoComplete: 'off' }}
        inputId={field.name}
        value={optionValue}
        // onBlur={handleBlur}
        onChange={handleChange}
        name={field.name}
        options={options}
        // react-select drops a falsy className, as it did the former `false`/`undefined`.
        className={touched[field.name] && errors[field.name] ? 'error-input' : undefined}
        styles={SelectCustomStyles}
        placeholder=""
        components={{ IndicatorSeparator: () => null }}
        isSearchable={isSearchable}
        blurInputOnSelect
      />
      <label className={classNameForLabel} id={`label-${field.name}`} htmlFor={field.name}>
        {spliceLabelOnMobile(label)}
      </label>
      {note ? (
        <div className="text-left">
          <small className="muted">{note}</small>
        </div>
      ) : null}
      {touched[field.name] && errors[field.name] && (
        <div className="error-text">{errors[field.name]}</div>
      )}
    </div>
  );
};

export default memo(Select, (prevProps, nextProps) =>
  areEqualFields(prevProps, nextProps, ['options']),
);
